import { Button } from "@/components/ui/button";
import { Reveal } from "./Reveal";
import { useHealthPackages } from "@/hooks/usePackages";
import type { PackagesSettings as PackagesSettingsData } from "@/hooks/useHomeSettings";
import { useState } from "react";
import { Check, Sparkles, Loader2, ChevronDown, ChevronUp } from "lucide-react";

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return "Not Disclosed";
  return `NPR ${Number(price).toLocaleString("en-IN")}`;
}

type PackageCardProps = {
  p: any;
  idx: number;
  mostPopularLabel: string;
};

function PackageCard({ p, idx, mostPopularLabel }: PackageCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? p.items : p.items.slice(0, 6);
  const hasMore = p.items.length > 6;

  return (
    <Reveal delay={idx * 120} className="h-full">
      <div
        className={`hover-lift relative flex h-full flex-col rounded-3xl p-8 transition-all duration-300 shadow-xl ${
          p.most_popular
            ? "bg-[#F7F4EA] border border-amber-200/60 text-stone-900"
            : "bg-white/10 backdrop-blur-md border border-white/15 text-white"
        }`}
      >
        {/* Most Popular Badge with 'MOST POPULAR' text */}
        {p.most_popular && (
          <span className="absolute -top-3.5 left-6 flex items-center gap-1.5 rounded-full border border-amber-300/80 bg-gradient-to-r from-[#E7D6B1] to-[#C9A66B] px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#3D280A] shadow-md">
            <Sparkles className="h-3.5 w-3.5 fill-current" />
            {mostPopularLabel || "MOST POPULAR"}
          </span>
        )}

        <div className="flex h-full flex-col">
          {/* Card Title & Description */}
          <h3
            className={`font-serif text-3xl font-bold tracking-tight ${
              p.most_popular ? "text-[#A0672A]" : "text-white"
            }`}
          >
            {p.name}
          </h3>
          {p.description && (
            <p
              className={`mt-2 text-sm leading-relaxed ${
                p.most_popular ? "text-stone-700" : "text-stone-200"
              }`}
            >
              {p.description}
            </p>
          )}

          {/* Price Block */}
          <div
            className={`mt-6 rounded-2xl p-4 shadow-sm transition-colors ${
              p.most_popular
                ? "bg-[#094D41] text-white"
                : "bg-white/10 border border-white/10 text-white"
            }`}
          >
            <div className="flex items-end gap-2">
              <span className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                {formatPrice(p.price)}
              </span>
              <span
                className={`pb-1 text-sm font-normal ${
                  p.most_popular ? "text-emerald-100/80" : "text-stone-300"
                }`}
              >
                / package
              </span>
            </div>
          </div>

          {/* Included Tests List */}
          <div className="mt-6 flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-semibold ${
                  p.most_popular ? "text-stone-900" : "text-white"
                }`}
              >
                Package Includes
              </span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                  p.most_popular
                    ? "bg-[#094D41] text-white"
                    : "bg-white/20 text-white"
                }`}
              >
                {p.items.length} Tests
              </span>
            </div>

            <ul className="mt-4 space-y-3">
              {visibleItems.map((i: any) => (
                <li
                  key={i.item_code}
                  className={`flex items-start gap-2.5 text-sm font-medium ${
                    p.most_popular ? "text-stone-800" : "text-stone-200"
                  }`}
                >
                  <Check
                    className={`mt-0.5 h-4 w-4 shrink-0 stroke-[2.5] ${
                      p.most_popular ? "text-stone-900" : "text-emerald-400"
                    }`}
                  />
                  <span>{i.description}</span>
                </li>
              ))}
            </ul>

            {hasMore && (
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className={`mt-5 inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4 transition-colors ${
                  p.most_popular
                    ? "text-stone-900 hover:text-[#094D41]"
                    : "text-amber-300 hover:text-amber-200"
                }`}
              >
                {expanded ? (
                  <>
                    Show Less
                    <ChevronUp className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    +{p.items.length - 6} more tests
                    <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* CTA Button */}
          <Button
            className={`mt-8 w-full rounded-full py-6 text-sm font-bold uppercase tracking-wider shadow-md transition-all ${
              p.most_popular
                ? "bg-[#094D41] text-white hover:bg-[#06382F]"
                : "bg-white text-stone-900 hover:bg-stone-100"
            }`}
            onClick={() => {
              console.log(`Booking package: ${p.name}`);
            }}
          >
            Book Package
          </Button>
        </div>
      </div>
    </Reveal>
  );
}

export function Packages({ settings }: { settings?: PackagesSettingsData | null }) {
  const { packages, isLoading } = useHealthPackages();
  const displayed = packages.slice(0, 3);

  const heading = settings?.heading || "Health Packages";
  const title = settings?.title || "Preventive care that pays for itself";
  const description =
    settings?.description ||
    "Curated screening packages for every age and lifestyle. Book online and visit any day — reports delivered to your portal within 48 hours.";
  const mostPopularLabel = settings?.most_popular_label || "Most Popular";

  return (
    <section className="bg-[#04332B] py-24 text-white">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-12 max-w-2xl">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#D2A662]">
            <Sparkles className="h-4 w-4" />
            {heading}
          </p>
          <h2 className="font-serif text-3xl font-semibold md:text-5xl">{title}</h2>
          <p className="mt-4 text-stone-300 leading-relaxed">{description}</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-white/60" />
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-3">
            {displayed.map((p, idx) => (
              <PackageCard
                key={p.name}
                p={p}
                idx={idx}
                mostPopularLabel={mostPopularLabel}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}